import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';
import { useAuth } from '../context/AuthContext';

interface PhaseEffortRecord {
  effort_id: number;
  application_id_code: string;
  application_name?: string;
  integration_group_id?: string;
  group_name?: string;
  phase_name: 'Assessment_Planning' | 'Design_Architecture' | 'Migration_Execution' | 'Testing_Validation' | 'GoLive_Optimization';
  estimated_duration_days: number;
  last_updated_date?: string;
  updated_by?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface ApplicationOption {
  application_id: string;
  description: string;
  disposition?: string;
}

// Helper function to get disposition badge color
const getDispositionColor = (disposition?: string) => {
  switch (disposition) {
    case 'Retire': return { bg: '#ffebee', color: '#c62828' };
    case 'Re-Platform': return { bg: '#e3f2fd', color: '#1565c0' };
    case 'Re-Design': return { bg: '#fff3e0', color: '#e65100' };
    case 'Retain': return { bg: '#e8f5e9', color: '#2e7d32' };
    default: return { bg: '#f5f5f5', color: '#757575' };
  }
};

// Helper function to get deployment status badge color
const getDeploymentStatusColor = (status?: string) => {
  switch (status) {
    case 'Approved': return { bg: '#e8f5e9', color: '#2e7d32' };
    case 'In Development': return { bg: '#e3f2fd', color: '#1565c0' };
    case 'Deployed': return { bg: '#c8e6c9', color: '#1b5e20' };
    case 'On Hold': return { bg: '#fff3e0', color: '#e65100' };
    default: return { bg: '#f5f5f5', color: '#757575' };
  }
};

const PhaseEffortPlanning: React.FC = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState<PhaseEffortRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  // Map of application_id to deployment_status for applications past planning stage
  const [lockedApplications, setLockedApplications] = useState<Map<string, string>>(new Map());

  // Helper function to get current user's full name (updated)
  const getUserFullName = () => {
    if (currentUser?.name) {
      return currentUser.name;
    } else if (currentUser?.email) {
      return currentUser.email;
    }
    return 'Unknown User';
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  // Status filter: 'need_approval' (default), 'approved', 'in_development', 'deployed', 'on_hold', 'all'
  const [filterStatus, setFilterStatus] = useState<string>('need_approval');
  const [editingRecord, setEditingRecord] = useState<PhaseEffortRecord | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [formData, setFormData] = useState({
    effortId: '',
    applicationIdCode: '',
    groupingId: '',
    phaseName: '',
    estimatedDurationDays: '',
    updatedBy: ''
  });

  const [bulkFormData, setBulkFormData] = useState({
    applicationIdCode: '',
    groupingId: '',
    phases: {
      Assessment_Planning: { duration: '' },
      Design_Architecture: { duration: '' },
      Migration_Execution: { duration: '' },
      Testing_Validation: { duration: '' },
      GoLive_Optimization: { duration: '' }
    }
  });

  useEffect(() => {
    fetchRecords();
    fetchIntegrationGroups();
    fetchLockedApplications();
  }, []);

  // Fetch applications that are locked (Approved and onwards - cannot add/edit effort)
  const fetchLockedApplications = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      const result = await response.json();
      // API returns array directly, not wrapped in { success, data }
      const dataArray = Array.isArray(result) ? result : (result.data || []);
      // Lock applications that are Approved or beyond (In Development, Deployed, On Hold)
      const lockedStatuses = ['Approved', 'In Development', 'Deployed', 'On Hold'];
      const locked = new Map<string, string>();
      dataArray
        .filter((r: any) => lockedStatuses.includes(r.deployment_status))
        .forEach((r: any) => {
          locked.set(r.application_id, r.deployment_status);
        });
      setLockedApplications(locked);
    } catch (err) {
      console.error('Error fetching locked applications:', err);
    }
  };

  // Load applications when grouping ID changes in bulk form
  useEffect(() => {
    if (bulkFormData.groupingId) {
      fetchApplicationsForGroup(bulkFormData.groupingId);
    } else {
      setApplications([]);
    }
  }, [bulkFormData.groupingId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const fetchRecords = async () => {
    // Always refresh locked applications when fetching records
    fetchLockedApplications();

    try {
      const response = await authFetch('/api/phase-effort-planning');
      // Don't treat 404 as an error - it just means no data
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort by Effort ID in ascending order
        data.sort((a: PhaseEffortRecord, b: PhaseEffortRecord) => a.effort_id - b.effort_id);
        setRecords(data);
      } else {
        // API returned non-array (empty or error), treat as empty
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      // Only set error for actual failures, not empty data
      console.error('Error fetching phase effort records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/phase-effort-planning/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} phase effort records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Application ID/Code,Grouping ID,Phase Name,Estimated Duration Days,Updated By
APP1954,IG001,Assessment_Planning,15,John Smith
APP8416,IG002,Design_Architecture,30,Jane Doe`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'phase_effort_planning_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      alert('No data to download');
      return;
    }

    // Match template format: Application ID/Code,Grouping ID,Phase Name,Estimated Duration Days,Updated By
    const headers = ['Application ID/Code', 'Grouping ID', 'Phase Name', 'Estimated Duration Days', 'Updated By'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(record.integration_group_id || '').replace(/"/g, '""')}"`,
        `"${(record.phase_name || '').replace(/"/g, '""')}"`,
        record.estimated_duration_days || 0,
        `"${(record.updated_by || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phase_effort_planning_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.applicationIdCode) {
      setError('Application ID Code is required');
      return;
    }

    if (!formData.groupingId || !formData.groupingId.trim()) {
      setError('Grouping ID is required and cannot be empty');
      return;
    }

    if (!formData.estimatedDurationDays) {
      setError('Estimated Duration Days is required');
      return;
    }

    try {
      const payload = {
        effort_id: formData.effortId ? parseInt(formData.effortId) : null,
        application_id_code: formData.applicationIdCode,
        integration_group_id: formData.groupingId.trim(),
        phase_name: formData.phaseName || 'Assessment_Planning',
        estimated_duration_days: formData.estimatedDurationDays ? parseInt(formData.estimatedDurationDays) : null,
        updated_by: formData.updatedBy || ''
      };

      if (editingRecord) {
        const response = await authFetch(`/api/phase-effort-planning/${editingRecord.effort_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }
        setSuccess('Phase effort record updated successfully');
      } else {
        const response = await authFetch('/api/phase-effort-planning', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }
        setSuccess('Phase effort record created successfully');
      }

      fetchRecords();
      
      // Clear form but keep modal open
      setFormData({
        effortId: '',
        applicationIdCode: '',
        groupingId: '',
        phaseName: '',
        estimatedDurationDays: '',
        updatedBy: ''
      });
      
      // Clear any error messages
      setError('');
    } catch (err: any) {
      console.error('Failed to save phase effort record:', err);
      setError(err.message || 'Failed to save phase effort record');
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!bulkFormData.applicationIdCode) {
        setError('Application ID/Code is required');
        return;
      }

      if (!bulkFormData.groupingId || !bulkFormData.groupingId.trim()) {
        setError('Grouping ID is required and cannot be empty');
        return;
      }

      // Check if application is locked (Approved or beyond)
      if (lockedApplications.has(bulkFormData.applicationIdCode)) {
        const lockStatus = lockedApplications.get(bulkFormData.applicationIdCode);
        setError(`Cannot add effort records - application "${bulkFormData.applicationIdCode}" is in status "${lockStatus}". Applications that are Approved or beyond cannot have effort records added.`);
        return;
      }

      const recordsToCreate: Array<{
        application_id_code: string;
        integration_group_id: string;
        phase_name: string;
        estimated_duration_days: number;
        updated_by: string;
      }> = [];
      
      // Check each phase and create records for those with duration and team size
      Object.entries(bulkFormData.phases).forEach(([phaseName, phaseData]) => {
        if (phaseData.duration) {
          recordsToCreate.push({
            application_id_code: bulkFormData.applicationIdCode,
            integration_group_id: bulkFormData.groupingId.trim(),
            phase_name: phaseName,
            estimated_duration_days: parseInt(phaseData.duration),
            updated_by: getUserFullName()
          });
        }
      });

      if (recordsToCreate.length === 0) {
        setError('Please fill in duration for at least one phase');
        return;
      }

      // Check for existing records (duplicates)
      const duplicates: string[] = [];
      for (const record of recordsToCreate) {
        const existingRecord = records.find(r => 
          r.application_id_code === record.application_id_code && 
          r.phase_name === record.phase_name
        );
        if (existingRecord) {
          const phaseLabelMap: {[key: string]: string} = {
            'Assessment_Planning': 'Assessment & Planning',
            'Design_Architecture': 'Design & Architecture', 
            'Migration_Execution': 'Migration Execution',
            'Testing_Validation': 'Testing & Validation',
            'GoLive_Optimization': 'Go-Live & Optimization'
          };
          duplicates.push(phaseLabelMap[record.phase_name] || record.phase_name);
        }
      }

      if (duplicates.length > 0) {
        setError(`Cannot create records - the following phase(s) already exist for application "${bulkFormData.applicationIdCode}": ${duplicates.join(', ')}. Please remove existing records first or use the edit function to update them.`);
        return;
      }

      // Create records sequentially
      let created = 0;
      for (const record of recordsToCreate) {
        const response = await authFetch('/api/phase-effort-planning', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(record)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to create record for ${record.phase_name}: ${errorData.error}`);
        }
        created++;
      }

      setSuccess(`Successfully created ${created} phase effort records`);
      fetchRecords();
      
      // Clear form
      setBulkFormData({
        applicationIdCode: '',
        groupingId: '',
        phases: {
          Assessment_Planning: { duration: '' },
          Design_Architecture: { duration: '' },
          Migration_Execution: { duration: '' },
          Testing_Validation: { duration: '' },
          GoLive_Optimization: { duration: '' }
        }
      });
      setError('');
    } catch (err: any) {
      console.error('Failed to create phase effort records:', err);
      setError(err.message || 'Failed to create phase effort records');
    }
  };

  const handleEdit = (record: PhaseEffortRecord) => {
    setEditingRecord(record);
    setViewMode(false);
    setFormData({
      effortId: record.effort_id.toString(),
      applicationIdCode: record.application_id_code,
      groupingId: record.integration_group_id || '',
      phaseName: record.phase_name,
      estimatedDurationDays: record.estimated_duration_days?.toString() || '',
      updatedBy: record.updated_by || ''
    });
    setShowForm(true);
  };

  const handleView = (record: PhaseEffortRecord) => {
    setEditingRecord(record);
    setViewMode(true);
    setFormData({
      effortId: record.effort_id.toString(),
      applicationIdCode: record.application_id_code,
      groupingId: record.integration_group_id || '',
      phaseName: record.phase_name,
      estimatedDurationDays: record.estimated_duration_days?.toString() || '',
      updatedBy: record.updated_by || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this phase effort record?')) {
      try {
        const response = await authFetch(`/api/phase-effort-planning/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Phase effort record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete phase effort record');
      }
    }
  };

  const handleDeleteAll = () => {
    setShowDeleteAllModal(true);
    setDeletePassword('');
  };

  const handleConfirmDeleteAll = async () => {
    if (deletePassword === 'DELETE2024!') {
      try {
        const response = await authFetch('/api/phase-effort-planning/all', {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete all');
        setSuccess('All phase effort records deleted successfully');
        fetchRecords();
        setShowDeleteAllModal(false);
        setDeletePassword('');
      } catch (err: any) {
        setError(err.message || 'Failed to delete all phase effort records');
      }
    } else {
      alert('Invalid password. Deletion cancelled.');
      setDeletePassword('');
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeletePassword('');
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setFormData({
      effortId: '',
      applicationIdCode: '',
      groupingId: '',
      phaseName: '',
      estimatedDurationDays: '',
      updatedBy: ''
    });
    setBulkFormData({
      applicationIdCode: '',
      groupingId: '',
      phases: {
        Assessment_Planning: { duration: '' },
        Design_Architecture: { duration: '' },
        Migration_Execution: { duration: '' },
        Testing_Validation: { duration: '' },
        GoLive_Optimization: { duration: '' }
      }
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setViewMode(false);
    setFormData({
      effortId: '',
      applicationIdCode: '',
      groupingId: '',
      phaseName: '',
      estimatedDurationDays: '',
      updatedBy: ''
    });
    setBulkFormData({
      applicationIdCode: '',
      groupingId: '',
      phases: {
        Assessment_Planning: { duration: '' },
        Design_Architecture: { duration: '' },
        Migration_Execution: { duration: '' },
        Testing_Validation: { duration: '' },
        GoLive_Optimization: { duration: '' }
      }
    });
    setError('');
  };

  return (
    <div className="page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header - Sticky */}
      <div className="page-header" style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'white', flexShrink: 0 }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Effort Planning</h1>
            <p className="page-header__subtitle">
              Manage migration phase effort planning and resource allocation
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new phase effort record"
            >
              ➕ Add Effort Record
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              📋 Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import phase effort records from CSV"
            >
              📤 Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading phase effort records...
        </div>
      )}

      {!loading && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {/* Filter Section - Side by side */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {/* Grouping ID Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '100px' }}>Grouping ID:</label>
              <input
                type="text"
                value={filterGroupingId}
                onChange={(e) => setFilterGroupingId(e.target.value)}
                placeholder="Filter by grouping..."
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
              {filterGroupingId && (
                <button
                  onClick={() => setFilterGroupingId('')}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Application ID Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '100px' }}>Application ID:</label>
              <input
                type="text"
                value={filterApplicationId}
                onChange={(e) => setFilterApplicationId(e.target.value)}
                placeholder="Filter by application..."
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '300px'
                }}
              />
              {filterApplicationId && (
                <button
                  onClick={() => setFilterApplicationId('')}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '60px' }}>Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '180px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="need_approval">Need Approval</option>
                <option value="approved">Approved</option>
                <option value="in_development">In Development</option>
                <option value="deployed">Deployed</option>
                <option value="on_hold">On Hold</option>
                <option value="all">All Statuses</option>
              </select>
            </div>
            
            {/* Filter Results Counter and Clear All Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {(() => {
                  const filteredRecords = records.filter(record => {
                    const groupingMatch = !filterGroupingId || record.integration_group_id?.toLowerCase().includes(filterGroupingId.toLowerCase());
                    const applicationMatch = !filterApplicationId ||
                      record.application_id_code.toLowerCase().includes(filterApplicationId.toLowerCase()) ||
                      record.application_name?.toLowerCase().includes(filterApplicationId.toLowerCase());

                    // Status filter logic
                    const appStatus = lockedApplications.get(record.application_id_code);
                    let statusMatch = true;
                    if (filterStatus === 'need_approval') {
                      // Show applications that don't have a status OR have "Need Approval" status
                      statusMatch = !appStatus || appStatus === 'Need Approval';
                    } else if (filterStatus === 'approved') {
                      statusMatch = appStatus === 'Approved';
                    } else if (filterStatus === 'in_development') {
                      statusMatch = appStatus === 'In Development';
                    } else if (filterStatus === 'deployed') {
                      statusMatch = appStatus === 'Deployed';
                    } else if (filterStatus === 'on_hold') {
                      statusMatch = appStatus === 'On Hold';
                    }

                    return groupingMatch && applicationMatch && statusMatch;
                  });
                  return `Showing ${filteredRecords.length} of ${records.length}`;
                })()}
              </span>
              {(filterGroupingId || filterApplicationId || filterStatus !== 'need_approval') && (
                <button
                  onClick={() => {
                    setFilterGroupingId('');
                    setFilterApplicationId('');
                    setFilterStatus('need_approval');
                  }}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#2196f3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon">📊</div>
            <h3>No Phase Effort Records Found</h3>
            <p>Use the action cards above to add phase effort records or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table" style={{ fontSize: '16px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap', width: '70px', maxWidth: '70px' }}>Effort ID</th>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Grouping</th>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Application</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Phase Name</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap', width: '60px', maxWidth: '60px' }}>Duration</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.filter(record => {
                  const groupingMatch = !filterGroupingId || record.integration_group_id?.toLowerCase().includes(filterGroupingId.toLowerCase());
                  const applicationMatch = !filterApplicationId ||
                    record.application_id_code.toLowerCase().includes(filterApplicationId.toLowerCase()) ||
                    record.application_name?.toLowerCase().includes(filterApplicationId.toLowerCase());

                  // Status filter logic
                  const appStatus = lockedApplications.get(record.application_id_code);
                  let statusMatch = true;
                  if (filterStatus === 'need_approval') {
                    // Show applications that don't have a status OR have "Need Approval" status
                    statusMatch = !appStatus || appStatus === 'Need Approval';
                  } else if (filterStatus === 'approved') {
                    statusMatch = appStatus === 'Approved';
                  } else if (filterStatus === 'in_development') {
                    statusMatch = appStatus === 'In Development';
                  } else if (filterStatus === 'deployed') {
                    statusMatch = appStatus === 'Deployed';
                  } else if (filterStatus === 'on_hold') {
                    statusMatch = appStatus === 'On Hold';
                  }
                  // 'all' shows everything

                  return groupingMatch && applicationMatch && statusMatch;
                }).map(record => (
                  <tr key={record.effort_id}>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap', width: '70px', maxWidth: '70px' }}>
                      {record.effort_id}
                    </td>
                    <td style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{record.integration_group_id || '-'}</div>
                        {record.group_name && (
                          <div style={{ fontSize: '14px', color: '#666' }}>{record.group_name}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{record.application_id_code}</div>
                        {record.application_name && (
                          <div style={{ fontSize: '14px', color: '#666' }}>{record.application_name}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>{record.phase_name.replace('_', ' ')}</td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap', width: '60px', maxWidth: '60px' }}>{record.estimated_duration_days || '-'}</td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        {/* Show deployment status badge if locked */}
                        {lockedApplications.has(record.application_id_code) && (
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: '500',
                              backgroundColor: getDeploymentStatusColor(lockedApplications.get(record.application_id_code)).bg,
                              color: getDeploymentStatusColor(lockedApplications.get(record.application_id_code)).color,
                              marginRight: '6px'
                            }}
                            title="This application is locked - cannot edit/delete effort records"
                          >
                            {lockedApplications.get(record.application_id_code)}
                          </span>
                        )}
                        <button
                          onClick={() => handleView(record)}
                          title="View details"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        {!lockedApplications.has(record.application_id_code) && (
                          <>
                            <button
                              onClick={() => handleEdit(record)}
                              title="Edit record"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(record.effort_id)}
                              title="Delete record"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#d32f2f',
                                border: '1px solid #d32f2f',
                                borderRadius: '4px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && previewData.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '24px 24px 16px 24px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '500' }}>Upload Phase Effort Records</h2>
              <button
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
                onClick={() => {
                  setShowUploadModal(false);
                  setPreviewData([]);
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ marginBottom: '16px', fontSize: '14px', color: '#424242' }}>Found {previewData.length} phase effort records to import</p>
              <div style={{ 
                maxHeight: '400px', 
                overflowY: 'auto', 
                marginBottom: '16px', 
                border: '1px solid #e0e0e0', 
                borderRadius: '4px' 
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: '#757575' }}>Application ID/Code</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: '#757575' }}>Grouping ID</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: '#757575' }}>Phase Name</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: '#757575' }}>Estimated Duration Days</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: '#757575' }}>Updated By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px', borderTop: '1px solid #e0e0e0', fontSize: '12px' }}>{row['Application ID/Code'] || ''}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #e0e0e0', fontSize: '12px' }}>{row['Grouping ID'] || ''}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #e0e0e0', fontSize: '12px' }}>{row['Phase Name'] || ''}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #e0e0e0', fontSize: '12px' }}>{row['Estimated Duration Days'] || ''}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #e0e0e0', fontSize: '12px' }}>{row['Updated By'] || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 5 && (
                  <p style={{ fontSize: '12px', color: '#757575', textAlign: 'center', marginTop: '8px' }}>
                    Showing first 5 records. {previewData.length - 5} more records will be imported.
                  </p>
                )}
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px' 
              }}>
                <button
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onClick={() => {
                    setShowUploadModal(false);
                    setPreviewData([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onClick={handleBulkImport}
                >
                  📤 Import {previewData.length} Phase Effort Records
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="md-modal">
          <div className="md-modal-content">
            <div className="md-modal-header">
              <h2>Confirm Delete All Phase Effort Records</h2>
              <button 
                className="md-close-button"
                onClick={handleCancelDeleteAll}
              >
                ×
              </button>
            </div>
            <div className="md-modal-body">
              <p>This action will permanently delete all phase effort records from the database.</p>
              <div style={{ margin: '20px 0' }}>
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px', 
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <p>⚠️ <strong>Warning:</strong></p>
                  <p>You are about to permanently delete <strong>{records.length} phase effort records</strong> from the database.</p>
                  <p>This action cannot be undone.</p>
                </div>
                <label>
                  <strong>Type "DELETE2024!" to confirm:</strong>
                  <input
                    type="text"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="md-input"
                    style={{ marginTop: '8px' }}
                  />
                </label>
              </div>
            </div>
            <div className="md-modal-footer">
              <button 
                className="md-button md-button--outlined"
                onClick={handleCancelDeleteAll}
              >
                Cancel
              </button>
              <button 
                className="md-button md-button--danger"
                onClick={handleConfirmDeleteAll}
                disabled={deletePassword !== 'DELETE2024!'}
              >
                🗑️ Delete All Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase Effort Record Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '700px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '3px solid #1976d2',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '500', color: '#212121'}}>
                {viewMode ? 'View Effort Record' : editingRecord ? 'Edit Effort Record' : 'Add New Effort Record'}
              </h2>
              <button
                onClick={handleCloseForm}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              {editingRecord ? (
                // Single record edit form
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px', fontSize: '16px' }}>
                      {error}
                    </div>
                  )}

                  <div className="md-form-section">
                    <h3 style={{fontSize: '16px'}}>Basic Information</h3>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label style={{fontSize: '16px'}}>Effort ID (Primary Key)</label>
                        <input
                          type="number"
                          className="md-input"
                          style={{fontSize: '16px', padding: '12px'}}
                          value={formData.effortId}
                          onChange={(e) => setFormData({...formData, effortId: e.target.value})}
                          placeholder="Leave empty for auto-increment"
                          disabled={!!editingRecord}
                        />
                      </div>
                      <div className="md-form-group">
                        <label style={{fontSize: '16px'}}>Grouping ID *</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{fontSize: '16px', padding: '12px', backgroundColor: viewMode ? '#f5f5f5' : 'white'}}
                          value={formData.groupingId}
                          onChange={(e) => setFormData({...formData, groupingId: e.target.value})}
                          required
                          placeholder="e.g., IG001"
                          disabled={viewMode}
                        />
                      </div>
                      <div className="md-form-group">
                        <label style={{fontSize: '16px'}}>Application ID/Code *</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{fontSize: '16px', padding: '12px', backgroundColor: viewMode ? '#f5f5f5' : 'white'}}
                          value={formData.applicationIdCode}
                          onChange={(e) => setFormData({...formData, applicationIdCode: e.target.value})}
                          required
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label style={{fontSize: '16px'}}>Updated By</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{fontSize: '16px', padding: '12px', backgroundColor: viewMode ? '#f5f5f5' : 'white'}}
                          value={formData.updatedBy}
                          onChange={(e) => setFormData({...formData, updatedBy: e.target.value})}
                          placeholder="Your name"
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                  </div>

                    <div className="md-form-section">
                      <h3 style={{fontSize: '16px'}}>Phase Details</h3>
                      <div className="md-form-row">
                        <div className="md-form-group">
                          <label style={{fontSize: '16px'}}>Phase Name</label>
                          <select
                            className="md-select"
                            style={{fontSize: '16px', padding: '12px', backgroundColor: viewMode ? '#f5f5f5' : 'white'}}
                            value={formData.phaseName}
                            onChange={(e) => setFormData({...formData, phaseName: e.target.value})}
                            disabled={viewMode}
                          >
                            <option value="">Select Phase</option>
                            <option value="Assessment_Planning">Assessment & Planning</option>
                            <option value="Design_Architecture">Design & Architecture</option>
                            <option value="Migration_Execution">Migration Execution</option>
                            <option value="Testing_Validation">Testing & Validation</option>
                            <option value="GoLive_Optimization">Go-Live & Optimization</option>
                          </select>
                        </div>
                      </div>
                    </div>

                  <div className="md-form-section">
                    <h3 style={{fontSize: '16px'}}>Duration</h3>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label style={{fontSize: '16px'}}>Estimated Duration Days</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{fontSize: '16px', padding: '12px', backgroundColor: viewMode ? '#f5f5f5' : 'white'}}
                          value={formData.estimatedDurationDays}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setFormData({...formData, estimatedDurationDays: value});
                          }}
                          placeholder="Calendar days"
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                    >
                      {viewMode ? 'Close' : 'Cancel'}
                    </button>
                    {!viewMode && (
                      <button
                        type="submit"
                        style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                      >
                        Update Effort Record
                      </button>
                    )}
                  </div>
                </form>
              ) : (
                // Bulk creation form for all phases
                <form onSubmit={handleBulkSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px', fontSize: '16px' }}>
                      {error}
                    </div>
                  )}

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{fontSize: '16px', margin: '0 0 16px 0', fontWeight: '500'}}>Application Information</h3>

                    {/* Grouping ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.groupingId}
                        onChange={(e) => {
                          setBulkFormData({
                            ...bulkFormData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{fontSize: '16px', fontWeight: '500', marginBottom: '8px', display: 'block'}}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative' }}>
                        <div
                          onClick={() => {
                            if (bulkFormData.groupingId) {
                              setShowAppDropdown(!showAppDropdown);
                            }
                          }}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: !bulkFormData.groupingId ? '#f5f5f5' : 'white',
                            cursor: bulkFormData.groupingId ? 'pointer' : 'not-allowed',
                            fontSize: '16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ color: bulkFormData.applicationIdCode ? '#212121' : '#9e9e9e', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {bulkFormData.applicationIdCode && applications.find(a => a.application_id === bulkFormData.applicationIdCode)
                              ? (
                                <>
                                  <span>{applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.application_id} - {applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.description}</span>
                                  {applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '16px',
                                      fontWeight: '500',
                                      backgroundColor: getDispositionColor(applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition).bg,
                                      color: getDispositionColor(applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition).color
                                    }}>
                                      {applications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (bulkFormData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {bulkFormData.groupingId && <span>▼</span>}
                        </div>
                        {showAppDropdown && bulkFormData.groupingId && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                            {applications.filter(app => !lockedApplications.has(app.application_id)).length === 0 ? (
                              <div style={{ padding: '12px 16px', color: '#9e9e9e', fontSize: '16px' }}>
                                {applications.length === 0
                                  ? 'No applications available for this group'
                                  : 'All applications in this group are locked (Approved or beyond)'}
                              </div>
                            ) : (
                              applications.map(app => {
                                const isLocked = lockedApplications.has(app.application_id);
                                const lockStatus = lockedApplications.get(app.application_id);
                                return (
                                  <div
                                    key={app.application_id}
                                    onClick={() => {
                                      if (!isLocked) {
                                        setBulkFormData({...bulkFormData, applicationIdCode: app.application_id});
                                        setShowAppDropdown(false);
                                      }
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      cursor: isLocked ? 'not-allowed' : 'pointer',
                                      fontSize: '16px',
                                      borderBottom: '1px solid #f0f0f0',
                                      backgroundColor: isLocked ? '#f5f5f5' : (bulkFormData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white'),
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      opacity: isLocked ? 0.6 : 1
                                    }}
                                    onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                    onMouseLeave={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = bulkFormData.applicationIdCode === app.application_id ? '#e3f2fd' : 'white'; }}
                                    title={isLocked ? `Cannot add effort - application is ${lockStatus}` : ''}
                                  >
                                    <span><strong>{app.application_id}</strong> - {app.description}</span>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                      {isLocked && (
                                        <span style={{
                                          padding: '2px 8px',
                                          borderRadius: '10px',
                                          fontSize: '11px',
                                          fontWeight: '500',
                                          backgroundColor: getDeploymentStatusColor(lockStatus).bg,
                                          color: getDeploymentStatusColor(lockStatus).color
                                        }}>
                                          {lockStatus}
                                        </span>
                                      )}
                                      {app.disposition && !isLocked && (
                                        <span style={{
                                          padding: '2px 8px',
                                          borderRadius: '10px',
                                          fontSize: '11px',
                                          fontWeight: '500',
                                          backgroundColor: getDispositionColor(app.disposition).bg,
                                          color: getDispositionColor(app.disposition).color
                                        }}>
                                          {app.disposition}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{fontSize: '16px', margin: '0 0 12px 0', fontWeight: '500'}}>Phase Effort Planning</h3>
                    <p style={{color: '#666', fontSize: '16px', marginBottom: '16px', lineHeight: '1.4'}}>
                      Fill Duration for phases to create. Only phases with duration values will create records.
                    </p>

                    <div style={{display: 'grid', gap: '12px'}}>
                      {Object.entries({
                        Assessment_Planning: 'Assessment & Planning',
                        Design_Architecture: 'Design & Architecture',
                        Migration_Execution: 'Migration Execution',
                        Testing_Validation: 'Testing & Validation',
                        GoLive_Optimization: 'Go-Live & Optimization'
                      }).map(([phaseKey, phaseLabel]) => (
                        <div key={phaseKey} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#fafafa'
                        }}>
                          <div style={{fontWeight: '500', color: '#333', fontSize: '16px'}}>{phaseLabel}</div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <input
                              type="text"
                              className="md-input"
                              style={{
                                padding: '10px 12px',
                                fontSize: '16px',
                                height: '44px',
                                width: '80px',
                                textAlign: 'right',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                              value={bulkFormData.phases[phaseKey as keyof typeof bulkFormData.phases].duration}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setBulkFormData({
                                  ...bulkFormData,
                                  phases: {
                                    ...bulkFormData.phases,
                                    [phaseKey]: {
                                      ...bulkFormData.phases[phaseKey as keyof typeof bulkFormData.phases],
                                      duration: value
                                    }
                                  }
                                });
                              }}
                              placeholder="0"
                              title="Duration (days)"
                            />
                            <span style={{fontSize: '16px', color: '#666', minWidth: '35px'}}>days</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                    >
                      Create Effort Records
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      </div>
      )}
    </div>
  );
};

export default PhaseEffortPlanning;